
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ErrorBanner } from '../../components/ErrorBanner';
import { useCurrentUser, useLibraryFacets } from '../../hooks';
import type { Id, Insert } from '../../types/api';
import { EnrichPanel } from './EnrichPanel';
import {
  DEFAULT_FILTERS,
  PAGE_SIZE,
  filterProblems,
  isSemantic,
  type LibraryFilters,
} from './filters';
import { InsertDetail } from './InsertDetail';
import { SearchResults, ShowcaseResults, type ResultsShellProps } from './LibraryResults';
import { LibraryToolbar } from './LibraryToolbar';
import { UploadPanel } from './UploadPanel';
import styles from './LibraryPage.module.css';

const QUERY_DEBOUNCE_MS = 400;

const FACET_LIMIT = 50;

export function LibraryPage() {
  const { can } = useCurrentUser();
  const canEdit = can('editor');

  const [filters, setFilters] = useState<LibraryFilters>(DEFAULT_FILTERS);
  const [queryDraft, setQueryDraft] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [showUpload, setShowUpload] = useState(false);
  const [showEnrich, setShowEnrich] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedId = (searchParams.get('item') || null) as Id | null;

  const facetsQuery = useLibraryFacets(FACET_LIMIT);
  const facets = facetsQuery.data;

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = queryDraft.trim();
      setFilters((current) => (current.q === next ? current : { ...current, q: next }));
    }, QUERY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [queryDraft]);

  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [filters]);

  const patch = useCallback((changes: Partial<LibraryFilters>) => {
    setFilters((current) => ({ ...current, ...changes }));
  }, []);

  const reset = useCallback(() => {
    setQueryDraft('');
    setFilters(DEFAULT_FILTERS);
  }, []);

  const openItem = useCallback(
    (item: Insert) => {
      const next = new URLSearchParams(searchParams);
      next.set('item', item.id);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const closeItem = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('item');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const problems = useMemo(() => filterProblems(filters), [filters]);
  const untagged = facets ? facets.total - facets.tagged : 0;
  const semantic = isSemantic(filters);

  const uploadBlockedReason = canEdit
    ? null
    : 'Роль «наблюдатель» не загружает материалы';

  const shell: ResultsShellProps = {
    filters,
    limit,
    onMore: () => setLimit((current) => current + PAGE_SIZE),
    facets,
    selectedId,
    onOpen: openItem,
    onReset: reset,
    canEdit,
    onEnrichClick: () => setShowEnrich(true),
    onUploadClick: () => setShowUpload(true),
    renderToolbar: (found, loading) => (
      <>
        {!canEdit && (
          <div className={styles.notice}>
            Роль «наблюдатель»: смотреть можно всё, менять библиотеку нельзя.
          </div>
        )}
        {facetsQuery.error && (
          <ErrorBanner
            error={facetsQuery.error}
            onRetry={() => void facetsQuery.refetch()}
          />
        )}
        <LibraryToolbar
          filters={filters}
          onChange={patch}
          queryDraft={queryDraft}
          onQueryDraft={setQueryDraft}
          onReset={reset}
          facets={facets}
          facetsLoading={facetsQuery.isLoading}
          found={found}
          loading={loading}
          onUploadClick={() => setShowUpload((current) => !current)}
          uploadBlockedReason={uploadBlockedReason}
          problems={problems}
        />
      </>
    ),
    panels: (
      <>
        {showUpload && <UploadPanel onClose={() => setShowUpload(false)} canEdit={canEdit} />}
        {showEnrich && (
          <EnrichPanel
            canEdit={canEdit}
            onClose={() => setShowEnrich(false)}
            untagged={untagged}
          />
        )}
      </>
    ),
  };

  return (
    <div className={selectedId ? styles.pageWithDetail : styles.page}>
      <div className={styles.column}>
        {semantic ? <SearchResults {...shell} /> : <ShowcaseResults {...shell} />}
      </div>

      {selectedId && (
        <InsertDetail itemId={selectedId} onClose={closeItem} canEdit={canEdit} />
      )}
    </div>
  );
}
